import '../../shared/style.css';
import { Button, Group, Modal, ModalDrawerControllerProps } from '@economic/gaudi';
import React, { useState } from 'react';

import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../shared/components/DisplayMessages';
import {
    InvoicingMessage,
    InvoicingMessageType,
    OrderSendESignatureRequest,
    SendByType,
} from '../../invoices/book-and-send/types';
import { useEmailOptionsPdfSelected, useEmailOptionsRecipientsSelected } from '../../invoices/utils';
import { emailTemplateUpdater } from '../../invoices/utils/emailTemplateUpdater';
import { EmailTemplatePicker } from '../../invoices/utils/EmailTemplatePicker';
import { EmailTemplatesFormState } from '../../invoices/utils/emailTemplateFormState';
import { SendAsEmail } from '../../shared/components/SendBy';
import { EmailOptionsDrawerToggle } from '../../shared/components/EmailOptionsDrawerToggle';
import { SalesModalFormWithEmailOptionsDrawer } from '../../shared/components/SalesModalFormWithEmailOptionsDrawer';
import { EsignatureOnboardingStatus } from '../../../../../../types/internal-api';
import { SendOrderESignatureSettings } from './SendOrderESignatureSettings';
import { FormApi } from 'final-form';

export type SendOrderFormData = {
    sendByType: SendByType;
    messages: InvoicingMessage[];
    emailOptions: {
        showPdf: boolean;
        recipients: string;
        recipientsCc: string;
        recipientsBcc: string;
        subject: string;
        saveSubject: boolean;
        body: string;
        saveBody: boolean;
        includePdfLink: boolean;
        encrypt: boolean;
        includePdfAsAttachment: boolean;
    };
    esignatureOnboardingStatus: EsignatureOnboardingStatus;
    orderSendESignatureRequest?: OrderSendESignatureRequest;
} & EmailTemplatesFormState;

function useRequiredFieldsValidation(shouldValidateRecipients = true): InvoicingMessage[] {
    const requiredFieldsMessages = [] as InvoicingMessage[];
    // check if email options make sense for sending order.
    const isPdfSelected = useEmailOptionsPdfSelected();
    // check if at least one recipient fields is entered
    const isRecipientSelected = useEmailOptionsRecipientsSelected();

    if (!isPdfSelected) {
        requiredFieldsMessages.push({ message: t`FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR`, type: InvoicingMessageType.Error });
    }

    if (shouldValidateRecipients && !isRecipientSelected) {
        requiredFieldsMessages.push({
            message: t`FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR`,
            type: InvoicingMessageType.Error,
        });
    }

    return requiredFieldsMessages;
}

type SendOrderDialogProps = {
    information?: SendOrderFormData;
    loading: boolean;
    onRegisterAndSend: (values: any) => Promise<any>;
    onHide: () => void;
    disclosure: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
    error: boolean;
};

export function SendOrderDialog(props: SendOrderDialogProps) {
    const { information, loading, onRegisterAndSend, onHide, disclosure, error } = props;
    const [registerOnlyButtonDisabled, setRegisterOnlyButtonDisabled] = useState<boolean>(false);
    const [shouldValidateRecipients, setShouldValidateRecipients] = useState<boolean>(true);

    function onFormAction(form: any, sendByType: SendByType) {
        form.change('sendByType', Number(sendByType));
        form.blur('sendByType');
        form.submit();
    }
    function onSigningTemplateChange(form: FormApi, signingTemplateId: string) {
        form.change('orderSendESignatureRequest.signingTemplateId', signingTemplateId);
        if (signingTemplateId === 'NOSIGNINGREQUIRED') {
            form.change(
                'orderSendESignatureRequest.recipientEmail',
                information !== undefined && information?.emailOptions?.recipients?.length > 0
                    ? typeof information?.emailOptions?.recipients === 'string'
                        ? information?.emailOptions?.recipients
                        : information?.emailOptions?.recipients[0]
                    : ''
            );
            form.change('orderSendESignatureRequest.recipientName', '');
            setShouldValidateRecipients(true);
        } else {
            if (shouldValidateRecipients) {
                setShouldValidateRecipients(false);
            }
        }
        setRegisterOnlyButtonDisabled(signingTemplateId !== 'NOSIGNINGREQUIRED');
    }

    function registerOnly(form: FormApi) {
        onFormAction(form, SendByType.undefined);
    }

    function registerAndSend(form: FormApi) {
        onFormAction(form, SendByType.email);
    }

    function updater() {
        return [emailTemplateUpdater()];
    }

    let errorMessages: InvoicingMessage[];
    if (error) {
        errorMessages = [{ type: InvoicingMessageType.Error, message: t`FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR` }];
    } else {
        errorMessages = information?.messages?.filter(m => m.type === InvoicingMessageType.Error) ?? [];
    }

    return (
        <SalesModalFormWithEmailOptionsDrawer
            loading={loading}
            errorTitle={t`FORM_BOOKANDSEND_ERRORS_HEADER`}
            errorMessages={errorMessages}
            ariaLabel={t(`FORM_BOOKANDSEND_ORDER_HEADER`)}
            disclosure={disclosure}
            onHide={onHide}
            className="book-and-send-dialog form"
            size="xs"
            initialData={information}
            onSubmit={onRegisterAndSend}
            updates={updater}>
            {(form, drawer) =>
                information && (
                    <SendOrderDialogContent
                        information={information}
                        drawer={drawer}
                        onRegisterOnly={() => registerOnly(form)}
                        onRegisterAndSend={() => registerAndSend(form)}
                        registerOnlyButtonDisabled={registerOnlyButtonDisabled}
                        onSigningTemplateChange={signingTemplateId => onSigningTemplateChange(form, signingTemplateId)}
                        shouldValidateRecipients={shouldValidateRecipients}
                    />
                )
            }
        </SalesModalFormWithEmailOptionsDrawer>
    );
}

type SendOrderModalContentProps = {
    information: SendOrderFormData;
    drawer: ModalDrawerControllerProps;
    onRegisterOnly: () => void;
    onRegisterAndSend: () => void;
    registerOnlyButtonDisabled: boolean;
    onSigningTemplateChange: (signingTemplateId: string) => void;
    shouldValidateRecipients: boolean;
};

function SendOrderDialogContent(props: SendOrderModalContentProps) {
    const {
        information,
        drawer,
        onRegisterOnly,
        onRegisterAndSend,
        registerOnlyButtonDisabled,
        onSigningTemplateChange,
        shouldValidateRecipients,
    } = props;
    const requiredFieldValidationMessages = useRequiredFieldsValidation(shouldValidateRecipients);
    const messages = [...(information.messages || []), ...requiredFieldValidationMessages];
    return (
        <>
            <Modal.Header>{t(`FORM_BOOKANDSEND_ORDER_HEADER`)}</Modal.Header>

            <Modal.Content>
                <SendAsEmail title={t`FORM_BOOKANDSEND_ORDER_TEXT`} />

                <EmailOptionsDrawerToggle
                    drawerVisible={drawer.visible}
                    onShowDrawer={drawer.show}
                    onToggleDrawer={drawer.toggle}
                />

                <EmailTemplatePicker templates={information.templates} />

                <SendOrderESignatureSettings
                    esignatureOnboardingStatus={information?.esignatureOnboardingStatus}
                    onSigningTemplateChange={onSigningTemplateChange}
                />

                <div className="book-and-send-dialog__messages">
                    <DisplayMessages messages={messages} />
                </div>

                <div className="book-and-send-dialog__options">
                    <Field.Checkbox name="emailOptions.showPdf" text={t`FORM_BOOKANDSEND_SHOW_ORDER`} />
                </div>

                <Field.Hidden name="sendByType" value={Number(SendByType.undefined)} />
            </Modal.Content>

            <Modal.Footer>
                <Group>
                    <Button
                        className="book-and-send-dialog__action-button"
                        disabled={registerOnlyButtonDisabled}
                        type="submit"
                        onClick={onRegisterOnly}>
                        {t`FORM_BOOKANDSEND_ORDER_REGISTERASSENT`}
                    </Button>
                    <Button
                        className="book-and-send-dialog__action-button"
                        appearance="primary"
                        type="submit"
                        disabled={!!requiredFieldValidationMessages.length}
                        onClick={onRegisterAndSend}>
                        {t`FORM_BOOKANDSEND_ORDER_REGISTERANDSEND`}
                    </Button>
                </Group>
            </Modal.Footer>
        </>
    );
}
